import {
  CONTENTA_AUTH0_ACCESS_TOKEN,
  CONTENTA_B2B_ORGANIZATION_ID,
  CONTENTA_TOKEN,
} from 'constants/storages-values';
import { getFromStorage, saveInStorage } from './storageUtils';
import { keyRole } from './isRole';

/** Cookie flags that survive the cross-site redirect back from Auth0. */
export const AUTH_STORAGE_OPTIONS = { sameSite: 'None', secure: true };

export const getAuth0AccessToken = (authResult = {}) =>
  authResult.accessToken || authResult.access_token || null;

const decodePayload = (token) => {
  if (typeof token !== 'string') return null;

  const parts = token.split('.');
  if (parts.length !== 3 || typeof globalThis.atob !== 'function') return null;

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const binary = globalThis.atob(padded);
    const bytes = Array.from(
      binary,
      (character) => `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`
    ).join('');

    return JSON.parse(decodeURIComponent(bytes));
  } catch {
    return null;
  }
};

export const getOrganizationIdFromToken = (token) => {
  const organizationId = decodePayload(token)?.org_id;

  return typeof organizationId === 'string' && organizationId
    ? organizationId
    : null;
};

/**
 * Which token the API gets as its bearer. B2B sessions need the id token: it
 * carries the organization (org_id) the API reads. Everyone else sends the
 * access token, as before 2026-09-11.
 *
 * ponytail: stopgap. With an id token the API looks the user up on Auth0
 * /userinfo, which only accepts access tokens, so /traveler/user,
 * /advisor/user and /booking/{id}/payment-options answered 500 whenever the
 * API's UserInfo cache had expired. contenta-api-v2 #156 reads the id token
 * claims instead; once it is deployed the id token can go back to being the
 * bearer for everyone. B2B stays exposed to that 500 until then.
 */
export const getApiToken = (authResult = {}) => {
  const idToken = authResult.idToken || authResult.id_token || null;
  const accessToken = authResult.accessToken || authResult.access_token || null;

  if (idToken && getOrganizationIdFromToken(idToken)) return idToken;
  // Some flows (code exchange) return an encrypted access token that only
  // Auth0 can read; the API cannot validate it, so keep the id token there.
  if (accessToken && decodePayload(accessToken)) return accessToken;

  return idToken || accessToken;
};

/**
 * Sessions opened while the id token was everyone's bearer still have it
 * stored: move non-B2B ones to the stored access token. If that one has
 * expired, authContext's usual checkSession renewal stores a fresh one.
 */
export const migrateStoredApiToken = () => {
  const apiToken = getFromStorage(CONTENTA_TOKEN);
  const accessToken = getFromStorage(CONTENTA_AUTH0_ACCESS_TOKEN);

  if (
    !apiToken ||
    !accessToken ||
    apiToken === accessToken ||
    getOrganizationIdFromToken(apiToken) ||
    !decodePayload(accessToken)
  ) {
    return apiToken;
  }

  saveInStorage(CONTENTA_TOKEN, accessToken, AUTH_STORAGE_OPTIONS);
  return accessToken;
};

export const getOrganizationIdFromAuthorizeUrl = (authorizeUrl) => {
  if (typeof authorizeUrl !== 'string') return null;

  try {
    return new URL(authorizeUrl).searchParams.get('organization') || null;
  } catch {
    return null;
  }
};

export const mergeTokenClaimsIntoUser = (user = {}, token) => {
  const currentRoles = user?.[keyRole];
  if (Array.isArray(currentRoles) && currentRoles.length > 0) return user;

  const tokenRoles = decodePayload(token)?.[keyRole];
  if (!tokenRoles || (Array.isArray(tokenRoles) && tokenRoles.length === 0)) {
    return user;
  }

  return {
    ...user,
    [keyRole]: Array.isArray(tokenRoles) ? tokenRoles : [tokenRoles],
  };
};

/**
 * Stores an Auth0 result: the access token for /userinfo, the API bearer
 * picked by getApiToken, and the B2B organization. The `org_id`
 * claim is persisted so later `checkSession` calls stay inside the B2B org.
 * Returns what was stored so callers can reuse it without re-reading storage.
 */
export const persistAuthResult = (authResult = {}) => {
  const apiToken = getApiToken(authResult);
  const accessToken = getAuth0AccessToken(authResult);
  const organizationId = getOrganizationIdFromToken(apiToken);

  if (accessToken) {
    saveInStorage(
      CONTENTA_AUTH0_ACCESS_TOKEN,
      accessToken,
      AUTH_STORAGE_OPTIONS
    );
  }
  if (apiToken) {
    saveInStorage(CONTENTA_TOKEN, apiToken, AUTH_STORAGE_OPTIONS);
  }
  if (organizationId) {
    saveInStorage(
      CONTENTA_B2B_ORGANIZATION_ID,
      organizationId,
      AUTH_STORAGE_OPTIONS
    );
  }

  return { apiToken, accessToken, organizationId };
};

/**
 * `checkSession({})` outside the organization renews a plain user session and
 * drops the B2B roles, so the stored (or token-embedded) org id is always sent.
 */
export const getCheckSessionOptions = () => {
  const organizationId =
    getFromStorage(CONTENTA_B2B_ORGANIZATION_ID) ||
    getOrganizationIdFromToken(getFromStorage(CONTENTA_TOKEN));

  return organizationId ? { organization: organizationId } : {};
};
